/**
 * Shared PBIS / MTSS / FBA vocabulary used across the app.
 *
 * The values here are drawn from publicly-documented behavior-support
 * frameworks — Positive Behavioral Interventions and Supports (PBIS),
 * Multi-Tiered System of Supports (MTSS), and Functional Behavior
 * Assessment / Behavior Intervention Plan (FBA/BIP) — none of which are
 * proprietary to any single vendor or district training package. Nothing
 * in this file reproduces or is derived from a copyrighted training
 * curriculum; the option banks are the common categorical language you'd
 * see in any FBA/BIP handbook.
 *
 * Keep the enum values stable — they're persisted to Supabase, referenced
 * in analytics groupings, and rendered in PDFs/exports. If a new option is
 * genuinely needed, add it here rather than typing a free-text value into
 * a form; the analytics rollups only work when the vocabulary is
 * consistent.
 */

// -- The four (+two) functions of behavior --------------------------------
//
// Attention / Escape / Tangible / Sensory are the canonical four. `automatic`
// is often listed as a fifth (self-reinforcing, no external maintainer);
// `unknown` is a legitimate answer during initial FBA data collection.
export const FUNCTIONS_OF_BEHAVIOR = [
  {
    value: 'attention',
    label: 'Attention',
    description: 'Gain adult or peer attention.',
    color: '#2563eb', // blue-600
    icon: 'Users',
  },
  {
    value: 'escape',
    label: 'Escape / Avoid',
    description: 'Avoid or delay a difficult or non-preferred task.',
    color: '#dc2626', // red-600
    icon: 'LogOut',
  },
  {
    value: 'tangible',
    label: 'Tangible',
    description: 'Gain access to a preferred item or activity.',
    color: '#d97706', // amber-600
    icon: 'Package',
  },
  {
    value: 'sensory',
    label: 'Sensory',
    description: 'Meet an internal sensory need.',
    color: '#7c3aed', // violet-600
    icon: 'Sparkles',
  },
  {
    value: 'automatic',
    label: 'Automatic',
    description: 'Self-reinforcing; no external maintainer.',
    color: '#0891b2', // cyan-600
    icon: 'Repeat',
  },
  {
    value: 'unknown',
    label: 'Unknown',
    description: 'Function not yet identified — needs more data.',
    color: '#6b7280', // gray-500
    icon: 'HelpCircle',
  },
];

export const FUNCTION_BY_VALUE = Object.fromEntries(
  FUNCTIONS_OF_BEHAVIOR.map((f) => [f.value, f])
);

// -- MTSS tiers ------------------------------------------------------------
export const MTSS_TIERS = [
  {
    value: 1,
    label: 'Tier 1 — Universal',
    description: 'School-wide expectations, positive climate, all students.',
    color: '#16a34a', // green-600
  },
  {
    value: 2,
    label: 'Tier 2 — Targeted',
    description: 'Small-group / targeted supports (e.g. CICO, behavior contracts).',
    color: '#d97706', // amber-600
  },
  {
    value: 3,
    label: 'Tier 3 — Intensive',
    description: 'Individualized FBA + BIP with wrap-around supports.',
    color: '#dc2626', // red-600
  },
];

export const MTSS_TIER_BY_VALUE = Object.fromEntries(
  MTSS_TIERS.map((t) => [t.value, t])
);

// -- ABC vocabulary --------------------------------------------------------
//
// Grouped so the form can render sensible optgroups; the analytics rollups
// operate on the `value` strings, not the group labels.

export const SETTING_ACTIVITIES = [
  { value: 'academics-ela-math', label: 'Academics (ELA / Math)' },
  { value: 'academics-ss-science', label: 'Academics (Social Studies / Science)' },
  { value: 'specials', label: 'Specials / Electives' },
  { value: 'small-group', label: 'Small Group' },
  { value: 'large-group', label: 'Large Group' },
  { value: 'independent-work', label: 'Independent Work' },
  { value: 'centers', label: 'Centers' },
  { value: 'transition', label: 'Transition' },
  { value: 'arrival-dismissal', label: 'Arrival / Dismissal' },
  { value: 'snack-meals', label: 'Snack / Meals' },
  { value: 'recess', label: 'Recess / Free Play' },
  { value: 'outdoor-play', label: 'Outdoor Play' },
  { value: 'rest-nap', label: 'Rest / Nap' },
  { value: 'bathroom', label: 'Bathroom' },
  { value: 'assembly', label: 'Assembly' },
  { value: 'bus', label: 'Bus' },
  { value: 'unstructured', label: 'Unstructured Time' },
  { value: 'other', label: 'Other' },
];

export const LOCATION_OPTIONS = [
  { value: 'classroom', label: 'Classroom' },
  { value: 'hallway', label: 'Hallway' },
  { value: 'cafeteria', label: 'Cafeteria' },
  { value: 'gymnasium', label: 'Gymnasium' },
  { value: 'library', label: 'Library' },
  { value: 'playground', label: 'Playground / Outside' },
  { value: 'bathroom', label: 'Bathroom' },
  { value: 'office', label: 'Office' },
  { value: 'bus', label: 'Bus' },
  { value: 'other', label: 'Other' },
];

export const ANTECEDENT_OPTIONS = [
  { value: 'task-demand', label: 'Task / demand given' },
  { value: 'denied-access', label: 'Denied access to item / activity' },
  { value: 'transition', label: 'Transition to new activity' },
  { value: 'told-no', label: 'Told "no" / "stop"' },
  { value: 'waiting', label: 'Waiting / turn-taking' },
  { value: 'peer-conflict', label: 'Peer interaction / conflict' },
  { value: 'attention-to-others', label: 'Adult attention on another student' },
  { value: 'alone', label: 'Alone / no attention' },
  { value: 'unstructured', label: 'Down time / unstructured' },
  { value: 'corrective-feedback', label: 'Corrective feedback' },
  { value: 'sensory-overload', label: 'Sensory overload (noise, light, crowd)' },
  { value: 'schedule-change', label: 'Schedule / routine change' },
  { value: 'non-preferred-activity', label: 'Non-preferred activity' },
  { value: 'physical-prompt', label: 'Physical prompt' },
  { value: 'unknown', label: 'Unknown / not observed' },
];

export const BEHAVIOR_TYPE_OPTIONS = [
  { value: 'verbal-aggression', label: 'Verbal aggression' },
  { value: 'physical-aggression', label: 'Physical aggression' },
  { value: 'self-injury', label: 'Self-injury' },
  { value: 'property-destruction', label: 'Property destruction' },
  { value: 'non-compliance', label: 'Non-compliance / refusal' },
  { value: 'disruption', label: 'Disruption / calling out' },
  { value: 'elopement', label: 'Elopement' },
  { value: 'inappropriate-language', label: 'Inappropriate language' },
  { value: 'stereotypy', label: 'Stereotypy / repetitive behavior' },
  { value: 'crying', label: 'Crying / vocal distress' },
  { value: 'withdrawal', label: 'Withdrawal / shutdown' },
  { value: 'other', label: 'Other (describe)' },
];

export const CONSEQUENCE_OPTIONS = [
  { value: 'verbal-redirect', label: 'Verbal redirection' },
  { value: 'physical-redirect', label: 'Physical redirection' },
  { value: 'reminded-of-expectations', label: 'Reminded of expectations' },
  { value: 'offered-choice', label: 'Offered choice' },
  { value: 'offered-break', label: 'Offered / given break' },
  { value: 'given-item-activity', label: 'Given the item / activity' },
  { value: 'demand-removed', label: 'Task / demand removed or delayed' },
  { value: 'peer-attention', label: 'Peer attention' },
  { value: 'teacher-attention', label: 'Teacher attention' },
  { value: 'ignored', label: 'Ignored / planned ignoring' },
  { value: 'time-out', label: 'Time-out / separated from group' },
  { value: 'removed-from-activity', label: 'Removed from activity' },
  { value: 'office-referral', label: 'Office referral' },
  { value: 'physical-assist', label: 'Physical assist / hold' },
  { value: 'other', label: 'Other' },
];

export const STUDENT_REACTION_OPTIONS = [
  { value: 'stopped', label: 'Behavior stopped' },
  { value: 'continued', label: 'Behavior continued' },
  { value: 'intensified', label: 'Behavior intensified' },
];

export const INTERVENTION_OPTIONS = [
  { value: 'de-escalation', label: 'De-escalation techniques' },
  { value: 'sensory-break', label: 'Sensory / movement break' },
  { value: 'choice-making', label: 'Choice-making' },
  { value: 'visual-supports', label: 'Visual supports / schedule' },
  { value: 'first-then', label: 'First / Then board' },
  { value: 'break-card', label: 'Break card' },
  { value: 'cico', label: 'Check-In / Check-Out (CICO)' },
  { value: 'token-economy', label: 'Token economy' },
  { value: 'behavior-contract', label: 'Behavior contract' },
  { value: 'behavior-specific-praise', label: 'Behavior-specific praise' },
  { value: 'proximity', label: 'Proximity control' },
  { value: 'peer-support', label: 'Peer support' },
  { value: 'environmental-modification', label: 'Environmental modification' },
  { value: 'none', label: 'None used' },
];

// Common antecedent interventions (proactive strategies) for BSP planning.
// These are the "what we do before the behavior can happen" options.
export const ANTECEDENT_INTERVENTION_OPTIONS = [
  'Post & pre-teach visual schedule',
  'First / Then board for non-preferred tasks',
  'Pre-correction before difficult transitions',
  'Provide choice within task',
  'Modify task difficulty / chunking',
  'Movement / sensory break scheduled proactively',
  'Preferred seating / proximity to adult',
  'Visual timer for transitions',
  'Break card / "I need a break" signal',
  'Noise-reducing headphones',
  'Reduced work demand with fade-in plan',
  'Behavior-specific praise (4:1 positive ratio)',
  'Priming for schedule changes',
];

export const CONSEQUENCE_INTERVENTION_OPTIONS = [
  'Reinforce replacement behavior immediately',
  'Neutral verbal redirect + reteach expectation',
  'Planned ignoring for attention-maintained behavior',
  'Provide sensory break contingent on requesting',
  'Grant escape contingent on task completion',
  'Behavior-specific praise for expected behavior',
  'Token / point delivered for replacement behavior',
  'Access to preferred item contingent on asking',
  'Move to quiet / cool-down area (student-led)',
  'Escalation protocol: reduce demands, ensure safety',
];

export const TEACHING_STRATEGY_OPTIONS = [
  'Explicit instruction of replacement behavior',
  'Model + role-play with peers',
  'Practice during low-demand times',
  'Video modeling',
  'Social story / social narrative',
  'Self-monitoring checklist',
  'Peer-mediated practice',
];

// -- Team roles (team-based approach to FBA/BIP) --------------------------
export const TEAM_ROLES = [
  { value: 'teacher', label: 'Classroom Teacher', responsibility: 'Implements supports, collects data' },
  { value: 'special-ed-teacher', label: 'Special Ed Teacher', responsibility: 'Co-writes plan, coordinates services' },
  { value: 'paraprofessional', label: 'Paraprofessional', responsibility: 'Implements supports, collects data' },
  { value: 'administrator', label: 'Administrator', responsibility: 'Allocates resources, supports implementation' },
  { value: 'behavior-specialist', label: 'Behavior Specialist / BCBA', responsibility: 'Coaching, FBA, data review' },
  { value: 'school-psychologist', label: 'School Psychologist', responsibility: 'Assessment, evaluation' },
  { value: 'counselor', label: 'Counselor', responsibility: 'Social-emotional support, CICO mentor' },
  { value: 'family', label: 'Family / Caregiver', responsibility: 'Follow-through at home, communication' },
  { value: 'student', label: 'Student', responsibility: 'Self-monitoring, agrees to plan' },
  { value: 'related-service', label: 'Related Service (SLP / OT / PT)', responsibility: 'Domain-specific supports' },
];

// -- Escalation dos & don'ts ----------------------------------------------
//
// General crisis-prevention guidance common to most published escalation-
// cycle trainings (calm tone, brief directions, physical safety first).
// Nothing here is copied from a specific proprietary curriculum.
export const ESCALATION_DOS = [
  'Remain calm and use a low, steady voice',
  'Use brief, clear directions',
  'Reduce verbal interaction / limit talking',
  'Offer choices when appropriate',
  'Ensure physical safety of student and peers',
  'Give processing time — count silently to 10',
  'Move to a less crowded space if possible',
];

export const ESCALATION_DONTS = [
  'Argue or negotiate',
  'Lecture during escalation',
  'Engage in public power struggles',
  'Add new demands mid-crisis',
  'Threaten consequences',
  'Take the behavior personally',
];

// -- Behavior-specific praise phrase bank ----------------------------------
//
// Everyday English praise openers used to build behavior-specific praise
// (an evidence-based Tier 1 practice). The teacher pairs one of these
// phrases with the observed behavior — "Nice work waiting for your turn"
// — instead of generic "good job." These phrases are all common language;
// no specific published compilation is reproduced here.
export const PRAISE_PHRASES = [
  'You did a great job',
  'Nice work',
  'Fantastic',
  'You should be proud of',
  'I appreciate how you',
  'Wonderful',
  'I noticed you',
  'Excellent',
  "You've got the hang of",
  'Way to go',
  "You're really improving at",
  'Thank you for',
  'Awesome',
  'Keep it up',
  "That's exactly right",
  'Perfect',
  'You worked hard on',
  "You're such a great helper when you",
  'Impressive',
  "You've got this",
];
